import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import CryptoJS from "crypto-js";
import { PrimaryBtn } from "storybook-components/Button";
import { LinkStyle } from "storybook-components/Link";
import ErrorBoundary from "../ErrorBoundary";
import CirclePlus from "../../images/circle-plus.svg";

const WleForm = styled.form`
  display: flex;
  flex-direction: column;
  margin: auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  margin: 1rem 0 3rem;
`;

const SubmitButton = styled(PrimaryBtn)`
  flex: 1;
  margin: auto 0.5rem auto 0;
  &:last-child {
    margin: auto 0 auto 0.5rem;
  }
`;

const StyledLabel = styled.label`
  flex: 1;
  margin-bottom: 0.5rem;
`;

const StyledInput = styled.input`
  flex: 1;
  line-height: 1.5rem;
`;

const StyledCode = styled.code`
  color: ${({ theme }) => theme.colors.gray1200};
  margin: 2rem 0;
  padding: 1rem;
`;

const AddParameterButton = styled.button`
  display: flex;
  margin: 0.5rem 0;
  padding: 0;
  outline: none;
  border: none;
  background-color: transparent;
  cursor: pointer;
`;

const AddParameterIcon = styled.img`
  margin: auto 0.5rem auto 0;
`;

const RemoveParamButton = styled.button`
  flex: 0;
  padding: 0;
  outline: none;
  border: none;
  background-color: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.red500};
  font-weight: 500;
  font-size: 0.9rem;
  letter-spacing: -0.01rem;
`;

const ParamLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ParamInputGroup = styled.div`
  display: flex;
  margin: 0.5rem 0;
`;

const ParamInput = styled.input`
  flex: 1;
  line-height: 1.5rem;
  margin: auto 0.5rem auto 0;
  &:last-child {
    margin: auto 0 auto 0.5rem;
  }
`;

const ViewEmbedLink = styled.a`
  ${LinkStyle}
  margin: auto 0.5rem auto 0;
`;


const WhiteLabelEmbedTester = () => {
  // FieldRefs
  const urlField = useRef(null);
  const maxAgeField = useRef(null);
  const keyField = useRef(null);
  const secretKeyField = useRef(null);
  const timestampField = useRef(null);
  const parameterFormGroup = useRef(null);

  // const parameterFields = [];
  // Form state values
  const [parameterFieldsCount, setParameterFieldsCount] = useState(0);
  const [parameterFields, setParameterFields] = useState([]);
  const [embedUrlResult, setEmbedUrlResult] = useState("");
  const [signatureResult, setSignatureResult] = useState("");
  const [signedEmebedUrlResult, setSignedEmebedUrlResult] = useState("");
  const [embedLinkHref, setEmbedLinkHref] = useState("");
  const [showEmbedContainer, setShowEmbedContainer] = useState(false);

  // WLE 10 second timer values
  const [timerIsActive, setTimerActive] = useState(false);
  const [secondsRemaining, updateSecondsRemaining] = useState(0);

  // WLE 10 second timer update handler
  useEffect(() => {
    let interval = null;
    if (secondsRemaining > 0 && timerIsActive) {
      interval = setInterval(() => {
        updateSecondsRemaining(secondsRemaining - 1);
      }, 1000);
    } else {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [secondsRemaining, timerIsActive]);

  // Event handler - Add parameter button
  const addParameter = e => {
    e.preventDefault();
    let removeParameter = () => {};
    const parameterGroupIndex = parameterFieldsCount;
    const parameterGroup = (
      <FormGroup key={`param-field-${parameterFieldsCount + 1}`}>
        <ParamLabelRow>
          <StyledLabel>{`Parameter ${parameterFieldsCount + 1}`}</StyledLabel>
          <RemoveParamButton
            onClick={e => removeParameter(e, parameterGroupIndex)}
          >
            remove
          </RemoveParamButton>
        </ParamLabelRow>
        <ParamInputGroup>
          <ParamInput
            placeholder="Name"
            type="text"
            name="param-name"
          ></ParamInput>
          <ParamInput
            placeholder="Value"
            type="text"
            name="param-value"
          ></ParamInput>
        </ParamInputGroup>
      </FormGroup>
    );
    const newParameterFields = parameterFields.concat();
    newParameterFields.push(parameterGroup);
    setParameterFields(newParameterFields);
    setParameterFieldsCount(parameterFieldsCount + 1);
    removeParameter = (e, parameterGroupIndex) => {
      e.preventDefault();
      setParameterFields(newParameterFields);
    };
  };

  const checkWLE = (url, timestamp, maxAge, key, secret) => {
    const staticParams = {
      timestamp: timestamp,
      access_key: key,
      max_age: maxAge || 0
    };

    const userParams = getUserParams();
    const embedUrl = makeEmbedUrl(url, staticParams, userParams);
    const signed = signUrl(embedUrl, key, secret, timestamp);

    const signature = signed["signature"];
    const signedUrl = signed["signed_url"];

    setEmbedUrlResult(embedUrl);
    setSignatureResult(signature);
    setSignedEmebedUrlResult(signedUrl);

    // Check if signature will be valid relative to current time
    // Check if current unix time in seconds is within 10 seconds of timestamp in form
    // - if so, start the 10 second countdown timer
    const currentTime = Math.floor(new Date().getTime() / 1000);
    const signatureValidSeconds = Number.parseInt(timestamp) + 10 - currentTime;
    if (signatureValidSeconds > 0) {
      updateSecondsRemaining(
        signatureValidSeconds > 10 ? 10 : signatureValidSeconds
      );
      setTimerActive(true);
    }
    setShowEmbedContainer(true);
    setEmbedLinkHref(signedUrl);
  };

  // Create signed embed signature
  const signUrl = (url, key, secret, timestamp) => {
    const requestType = "GET";
    const contentType = null;
    const contentBody = "";
    const contentDigest = CryptoJS.MD5(contentBody).toString(
      CryptoJS.enc.Base64
    );

    const requestString = [
      requestType,
      contentType,
      contentDigest,
      url,
      timestamp
    ].join(",");

    const signature = CryptoJS.HmacSHA256(requestString, secret).toString(
      CryptoJS.enc.Hex
    );

    const signedUrl = url + "&signature=" + signature;

    return { signed_url: signedUrl, signature: signature };
  };

  // Get additional params if present
  function getUserParams() {
    const params = [];
    for (let child of parameterFormGroup.current.children) {
      const name = encodeURIComponent(
        child.querySelector("[name='param-name']").value
      );
      const value = encodeURIComponent(
        child.querySelector("[name='param-value']").value
      );
      params.push([name, value]);
    }
    return params;
  }

  // Stitch together the embed URL
  const makeEmbedUrl = (url, staticParams, userParams) => {
    let embedUrl =
      url +
      "/embed?access_key=" +
      staticParams["access_key"] +
      "&max_age=" +
      staticParams["max_age"] +
      "&";

    userParams.sort((a, b) => (a[0] < b[0] ? -1 : 1));

    for (let i = 0; i < userParams.length; i++) {
      let name = userParams[i][0],
        value = userParams[i][1];
      embedUrl += "param_" + name + "=" + value + "&";
    }

    return (embedUrl += "timestamp=" + staticParams["timestamp"]);
  };

  // Handler for both form submission buttons
  const checkWleEbed = useCurrentTimeStamp => {
    const url = urlField.current.value.trim().replace(/\/+$/, "");
    let timestamp = timestampField.current.value.trim();
    const maxAge = maxAgeField.current.value.trim();
    const key = keyField.current.value.trim();
    const secret = secretKeyField.current.value.trim();

    if (timestamp.length === 0 || useCurrentTimeStamp) {
      timestamp = Math.floor(Date.now() / 1000);
      timestampField.current.value = timestamp;
    }

    checkWLE(url, timestamp, maxAge, key, secret);
  };

  return (
    <ErrorBoundary>
      <div className="container">
        <h2>Inputs</h2>
        <WleForm>
          <FormGroup>
            <StyledLabel>Report URL</StyledLabel>
            <StyledInput type="text" id="url" ref={urlField}></StyledInput>
          </FormGroup>
          <FormGroup>
            <StyledLabel>
              <a href="https://en.wikipedia.org/wiki/Unix_time">
                Unix timestamp
              </a>{" "}
              (leave blank to use current timestamp)
            </StyledLabel>
            <StyledInput
              type="text"
              id="timestamp"
              ref={timestampField}
            ></StyledInput>
          </FormGroup>
          <FormGroup>
            <StyledLabel>Max age in seconds</StyledLabel>
            <StyledInput
              type="text"
              id="max-age"
              ref={maxAgeField}
            ></StyledInput>
          </FormGroup>
          <FormGroup>
            <StyledLabel>Access key</StyledLabel>
            <StyledInput
              type="text"
              id="access-key"
              ref={keyField}
            ></StyledInput>
          </FormGroup>
          <FormGroup>
            <StyledLabel>Access secret</StyledLabel>
            <StyledInput
              type="password"
              id="access-secret"
              ref={secretKeyField}
            ></StyledInput>
          </FormGroup>
          <AddParameterButton onClick={e => addParameter(e)}>
            <AddParameterIcon
              src={CirclePlus}
              alt="Icon button to add a parameter"
            />
            <div className="add-icon">Add parameter</div>
          </AddParameterButton>
          <FormGroup ref={parameterFormGroup}>
            {parameterFields.map(field => {
              return field;
            })}
          </FormGroup>
        </WleForm>
        <ButtonGroup>
          <SubmitButton onClick={() => checkWleEbed(false)}>
            Check
          </SubmitButton>
          <SubmitButton onClick={() => checkWleEbed(true)}>
            Check with current timestamp
          </SubmitButton>
        </ButtonGroup>
        {showEmbedContainer && (
          <div>
            <h2>Results</h2>
            <div>
              <div>Embed URL</div>
              <pre>
                <StyledCode>{embedUrlResult}</StyledCode>
              </pre>
            </div>
            <div>
              <div>Signature</div>
              <pre>
                <StyledCode>{signatureResult}</StyledCode>
              </pre>
            </div>
            <div>
              <div>
                Signed embed URL (
                {secondsRemaining > 0
                  ? `valid for ${secondsRemaining} seconds`
                  : "expired - signature must be regenerated with new timestamp"}
                )
              </div>
              <pre>
                <StyledCode>{signedEmebedUrlResult}</StyledCode>
              </pre>
            </div>

            <p>
              <ViewEmbedLink
                target="_blank"
                rel="noreferrer"
                href={embedLinkHref}
              >
                View Embed
              </ViewEmbedLink>
               - We recommend using a browser window that's logged out of Mode.
            </p>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default WhiteLabelEmbedTester;
